/**
 * AccessGate — lightweight passcode gate for the internal/preview
 * deployment.
 *
 * Flow:
 *   1. On mount, calls GET /api/auth/status. If 200 → render children.
 *   2. If 401 → render a passcode prompt.
 *   3. On submit, POST /api/auth/verify {code}. On 200 the server sets
 *      the HttpOnly signed cookie; we re-check status and unlock.
 *   4. On 503 (gate-not-configured) → render a clear setup hint so the
 *      operator knows to set the env var.
 *
 * Public domain never mounts this — it only wraps InternalApp.
 */
import { useEffect, useState, useCallback } from "react";
import JourneyAvatar from "@/components/JourneyAvatar";
import * as API from "@/lib/api";

const STATES = {
  CHECKING: "checking",
  LOCKED: "locked",
  UNLOCKED: "unlocked",
  UNCONFIGURED: "unconfigured",
};

export default function AccessGate({ children }) {
  const [state, setState] = useState(STATES.CHECKING);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const checkStatus = useCallback(async () => {
    try {
      const data = await API.authStatus();
      if (data?.authorized) {
        setState(STATES.UNLOCKED);
      } else if (data && data.configured === false) {
        setState(STATES.UNCONFIGURED);
      } else {
        setState(STATES.LOCKED);
      }
    } catch (e) {
      // 401 = locked. 503 = gate not configured. anything else → locked
      // (fail safe so the assistant is never exposed accidentally).
      const status = e?.response?.status;
      const data = e?.response?.data;
      if (status === 503 || data?.reason === "gate-not-configured") {
        setState(STATES.UNCONFIGURED);
      } else {
        setState(STATES.LOCKED);
      }
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim() || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await API.authVerify(code.trim());
      setCode("");
      await checkStatus();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) {
        setState(STATES.UNCONFIGURED);
      } else {
        setError("Wrong code. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };


  if (state === STATES.UNLOCKED) {
    return children;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white grain flex items-center justify-center safe-x px-5 sm:px-8">
      <div className="relative z-10 w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <JourneyAvatar size={72} testId="access-gate-avatar" />
          <h1
            className="font-display text-2xl sm:text-3xl tracking-tight mt-5"
            data-testid="access-gate-heading"
          >
            Journey
          </h1>
          <p className="text-white/55 text-sm mt-2 max-w-xs leading-relaxed">
            This assistant is private. Enter the access code to continue.
          </p>
        </div>

        {state === STATES.CHECKING ? (
          <div
            data-testid="access-gate-loading"
            className="flex items-center justify-center text-white/40 text-sm h-12"
          >
            <span className="typing-dot mr-1">·</span>
            <span className="typing-dot mr-1">·</span>
            <span className="typing-dot">·</span>
          </div>
        ) : state === STATES.UNCONFIGURED ? (
          <div
            data-testid="access-gate-unconfigured"
            className="rounded-2xl border border-amber-400/30 bg-amber-500/5 p-5 text-amber-100/85 text-sm leading-relaxed"
          >
            <div className="font-display text-amber-200 mb-2">
              Gate not configured
            </div>
            <p className="mb-3">
              The server has no access code set yet. Set{" "}
              <code className="text-amber-200/90 bg-black/30 px-1.5 py-0.5 rounded">
                INTERNAL_ACCESS_CODE
              </code>{" "}
              in the backend environment, then refresh.
            </p>
            <p className="text-amber-100/55 text-xs">
              While unset, the assistant is reachable without a code — set the
              value before sharing the URL with anyone.
            </p>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="access-gate-form"
            className="space-y-3"
            autoComplete="off"
          >
            <input
              type="password"
              inputMode="text"
              autoComplete="off"
              autoFocus
              spellCheck={false}
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                if (error) setError("");
              }}
              placeholder="Access code"
              data-testid="access-gate-input"
              className="w-full rounded-xl bg-white/[0.04] border border-white/10 focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-400/20 px-4 py-3 text-base placeholder:text-white/30 font-mono tracking-wider"
            />
            <button
              type="submit"
              disabled={!code.trim() || submitting}
              data-testid="access-gate-submit"
              className="w-full rounded-xl bg-cyan-400 hover:bg-cyan-300 disabled:bg-cyan-400/40 disabled:cursor-not-allowed text-[#04141a] px-4 py-3 text-sm font-medium transition-colors"
            >
              {submitting ? "Verifying…" : "Continue"}
            </button>
            <div
              data-testid="access-gate-error"
              className={`h-5 text-xs text-rose-300/85 text-center transition-opacity ${
                error ? "opacity-100" : "opacity-0"
              }`}
            >
              {error || " "}
            </div>
          </form>
        )}

        <div className="text-center text-white/35 text-xs mt-10">
          Single-owner deployment · cookie persists ~90 days
        </div>
      </div>
    </div>
  );
}
